import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AdminNav from '../../../components/admin/nav';
import { showAlert } from '../../../utils/showAlert';

const apiUrl = process.env.REACT_APP_API_URL;

const HomepageScreen = () => {
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin || {};

  const [homepage, setHomepage] = useState([]);
  const [available, setAvailable] = useState([]);
  const [selectedToAdd, setSelectedToAdd] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const authHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    Authorization: userInfo?.token ? `Bearer ${userInfo.token}` : undefined,
  }), [userInfo]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${apiUrl}/api/category/homepage/list`);
      setHomepage(data?.homepage || []);
      setAvailable(data?.available || []);
      setError('');
    } catch (err) {
      console.error('Error fetching homepage list', err);
      setError('Failed to load homepage categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const reordered = Array.from(homepage);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setHomepage(reordered);

    try {
      await axios.put(
        `${apiUrl}/api/category/homepage/reorder`,
        { orderedIds: reordered.map((c) => c._id) },
        { headers: authHeaders }
      );
      showAlert('Order updated', 'success');
    } catch (err) {
      console.error('Error reordering homepage', err);
      showAlert('Failed to save order', 'error');
      fetchData();
    }
  };

  const handleAdd = async () => {
    if (!selectedToAdd) return;
    try {
      await axios.post(
        `${apiUrl}/api/category/homepage/add`,
        { categoryId: selectedToAdd },
        { headers: authHeaders }
      );
      setSelectedToAdd('');
      showAlert('Added to homepage', 'success');
      fetchData();
    } catch (err) {
      console.error('Error adding category', err);
      showAlert('Failed to add', 'error');
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this category from homepage?')) return;
    try {
      await axios.delete(`${apiUrl}/api/category/homepage/${id}`, {
        headers: authHeaders,
      });
      showAlert('Removed from homepage', 'success');
      fetchData();
    } catch (err) {
      console.error('Error removing category', err);
      showAlert('Failed to remove', 'error');
    }
  };

  return (
    <>
      <AdminNav isView={true} />
      <div className='container'>
        <div className='d-flex justify-content-between align-items-center mb-4'>
          <h3 className='mb-0'>Homepage Categories</h3>
        </div>

        <div className='card mb-4'>
          <div className='card-body'>
            <h6 className='mb-3'>Add a category to homepage</h6>
            <div className='d-flex gap-2 align-items-center' style={{ gap: '0.5rem' }}>
              <select
                className='form-select'
                value={selectedToAdd}
                onChange={(e) => setSelectedToAdd(e.target.value)}
                style={{ maxWidth: '320px' }}
              >
                <option value=''>-- Select a category --</option>
                {available.map((cat) => (
                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                ))}
              </select>
              <button
                className='btn btn-primary'
                onClick={handleAdd}
                disabled={!selectedToAdd}
                style={{ marginLeft: '0.5rem' }}
              >
                Add
              </button>
              <Link to='/admin/category/create' className='btn btn-outline-success' style={{ marginLeft: '0.5rem' }}>
                + Create New Category
              </Link>
            </div>
            {available.length === 0 && (
              <small className='text-muted d-block mt-2'>All active categories are already on the homepage.</small>
            )}
          </div>
        </div>

        <div className='card'>
          <div className='card-header'>
            <strong>Active homepage categories</strong>
            <small className='text-muted ms-2'>(drag to reorder)</small>
          </div>
          <div className='card-body'>
            {loading ? (
              <div>Loading...</div>
            ) : error ? (
              <div className='alert alert-danger'>{error}</div>
            ) : homepage.length === 0 ? (
              <div className='text-muted'>No categories on homepage yet. Add one above.</div>
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId='homepageCategories'>
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {homepage.map((cat, index) => (
                        <Draggable key={cat._id} draggableId={String(cat._id)} index={index}>
                          {(prov, snapshot) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              className='d-flex align-items-center mb-2 p-2'
                              style={{
                                ...prov.draggableProps.style,
                                backgroundColor: snapshot.isDragging ? '#f0f7ff' : '#fff',
                                border: '1px solid #e5e5e5',
                                borderRadius: '8px',
                              }}
                            >
                              <span
                                {...prov.dragHandleProps}
                                style={{ cursor: 'grab', padding: '0 12px', color: '#999', fontSize: '18px' }}
                                title='Drag to reorder'
                              >
                                ⠿
                              </span>
                              <span className='me-3' style={{ color: '#999', minWidth: '30px' }}>
                                {index + 1}.
                              </span>
                              <span className='flex-grow-1' style={{ fontSize: '14px' }}>
                                {cat.name}
                              </span>
                              <small className='text-muted me-3'>{cat.slug}</small>
                              <Link
                                to={`/admin/category/edit/${cat.slug}`}
                                className='btn btn-sm btn-outline-secondary me-2'
                              >
                                Edit
                              </Link>
                              <button
                                className='btn btn-sm btn-outline-danger'
                                onClick={() => handleRemove(cat._id)}
                                title='Remove from homepage'
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default HomepageScreen;
